package sarka.design.skoria.network

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

/**
 * In-memory cache backed by persistent TokenStorage.
 * On app launch, call TokenStore.load() to restore saved session.
 */
object TokenStore {
    private val _token = MutableStateFlow<String?>(null)
    val token: StateFlow<String?> = _token

    private val _username = MutableStateFlow<String?>(null)
    val username: StateFlow<String?> = _username

    private val storage = TokenStorage()

    /** Call on app startup to restore persisted session */
    suspend fun load() {
        _token.value = storage.getToken()
        _username.value = storage.getUsername()
    }

    suspend fun save(token: String, username: String) {
        _token.value = token
        _username.value = username
        storage.saveToken(token)
        storage.saveUsername(username)
    }

    suspend fun clear() {
        _token.value = null
        _username.value = null
        storage.clear()
    }

    val isLoggedIn: Boolean get() = _token.value != null
}

class AuthRepository(
    private val authApi: AuthApi = AuthApi()
) {

    suspend fun login(username: String, password: String): AuthResult {
        val result = authApi.login(username, password)
        if (result is AuthResult.Success) {
            TokenStore.save(result.response.token, result.response.username)
        }
        return result
    }

    suspend fun register(username: String, email: String, password: String): RegisterResult {
        // Don't save token during registration - user must verify email first
        return authApi.register(username, email, password)
    }

    suspend fun verifyEmail(username: String, code: String): Result<String> {
        val result = authApi.verifyEmail(username, code)
        if (result.isSuccess) {
            val authResponse = result.getOrNull()
            if (authResponse != null) {
                TokenStore.save(authResponse.token, authResponse.username)
            }
        }
        return result.map { "Email verified successfully" }
    }

    suspend fun resendCode(username: String): Result<String> {
        return authApi.resendCode(username)
    }

    suspend fun getUserEmail(username: String): Result<String> {
        return authApi.getUserEmail(username)
    }

    suspend fun logout() {
        TokenStore.clear()
    }
}
